import type { ColumnDef } from "@tanstack/react-table";
import { FiChevronRight } from "react-icons/fi";

import type { BaseRowItem } from "../../components/data-table";
import type { SurveyApi } from "../../lib/api";
import {
  formatCount,
  formatOrdinalDate,
  formatPercent,
  placementLabel,
  surveyStatusBadgeClass,
  surveyStatusLabel,
} from "./surveyTypes";

export type SurveyTableRow = BaseRowItem & {
  raw: SurveyApi;
  title: string;
  description: string | null;
  status: string;
  statusLabel: string;
  questionsLabel: string;
  placementsLabel: string;
  responsesLabel: string;
  partialsLabel: string | null;
  completionRate: number;
  completionLabel: string;
  createdLabel: string;
};

export function buildSurveyTableRows(surveys: SurveyApi[]): SurveyTableRow[] {
  return surveys.map((s) => {
    const questions = s.questions?.length ?? 0;
    const partials = s.stats?.partials ?? 0;

    return {
      id: s._id,
      raw: s,
      title: s.title,
      description: s.description ?? null,
      status: String(s.status).toUpperCase(),
      statusLabel: surveyStatusLabel(s.status),
      questionsLabel: `${questions} question${questions === 1 ? "" : "s"}`,
      placementsLabel:
        s.placements?.length > 0
          ? s.placements.map(placementLabel).join(", ")
          : "No placement",
      responsesLabel: formatCount(s.stats?.completions),
      partialsLabel:
        partials > 0 ? `${formatCount(partials)} partial` : null,
      completionRate: s.stats?.completionRate ?? 0,
      completionLabel: formatPercent(s.stats?.completionRate),
      createdLabel: formatOrdinalDate(s.createdAt),
    };
  });
}

/** Green above 70%, amber 40-70%, red below — a glanceable health read per row. */
function completionBarClass(rate: number): string {
  if (rate >= 70) return "bg-emerald-500";
  if (rate >= 40) return "bg-amber-400";
  return "bg-red-500";
}

type ColumnCallbacks = {
  onView: (row: SurveyTableRow) => void;
};

export function createSurveyTableColumns({
  onView,
}: ColumnCallbacks): ColumnDef<SurveyTableRow, unknown>[] {
  return [
    {
      accessorKey: "title",
      header: "Survey",
      meta: { wrap: true, maxWidth: "320px" },
      cell: ({ row }) => (
        <div className="min-w-[200px]">
          <p className="font-semibold text-white">{row.original.title}</p>
          {row.original.description && (
            <p className="mt-1 line-clamp-2 text-[11px] leading-relaxed text-[#869AA9]">
              {row.original.description}
            </p>
          )}
          <p className="mt-1 text-[11px] text-[#5C6B76]">
            {row.original.questionsLabel}
          </p>
        </div>
      ),
    },
    {
      accessorKey: "statusLabel",
      header: "Status",
      cell: ({ row }) => (
        <span
          className={`inline-flex rounded-full border px-2.5 py-0.5 text-[11px] font-semibold tracking-wide ${surveyStatusBadgeClass(
            row.original.status
          )}`}
        >
          {row.original.statusLabel}
        </span>
      ),
    },
    {
      accessorKey: "placementsLabel",
      header: "Shown in",
      meta: { hideBelow: "lg" as const, wrap: true, maxWidth: "180px" },
      cell: ({ getValue }) => (
        <span className="text-[12px] text-adminTable-muted">
          {String(getValue())}
        </span>
      ),
    },
    {
      accessorKey: "responsesLabel",
      header: "Responses",
      cell: ({ row }) => (
        <div>
          <p className="text-[14px] font-semibold tabular-nums text-white">
            {row.original.responsesLabel}
          </p>
          {row.original.partialsLabel && (
            <p className="mt-1 text-[11px] text-[#869AA9]">
              {row.original.partialsLabel}
            </p>
          )}
        </div>
      ),
    },
    {
      accessorKey: "completionRate",
      header: "Completion",
      meta: { hideBelow: "md" as const },
      cell: ({ row }) => (
        <div className="min-w-[90px]">
          <p className="text-[13px] font-semibold tabular-nums text-white">
            {row.original.completionLabel}
          </p>
          <span
            className="mt-1.5 block h-1 w-full overflow-hidden rounded-full bg-[#2C363E]"
            aria-hidden
          >
            <span
              className={`block h-full rounded-full ${completionBarClass(
                row.original.completionRate
              )}`}
              style={{
                width: `${Math.min(100, Math.max(0, row.original.completionRate))}%`,
              }}
            />
          </span>
        </div>
      ),
    },
    {
      accessorKey: "createdLabel",
      header: "Created",
      meta: { hideBelow: "lg" as const },
      cell: ({ getValue }) => (
        <span className="text-adminTable-muted">{String(getValue())}</span>
      ),
    },
    {
      id: "actions",
      header: "",
      meta: { align: "right" as const },
      cell: ({ row }) => (
        <button
          type="button"
          aria-label={`Open ${row.original.title}`}
          onClick={() => onView(row.original)}
          className="rounded-lg border border-[#3A4852] bg-[#1C2227] p-2 text-[#AEBCC6] transition hover:border-secondary_button hover:text-white"
        >
          <FiChevronRight className="text-base" />
        </button>
      ),
    },
  ];
}
